import * as React from "react";
import styled from "styled-components";
import theme from "styled-theming";
import * as S from "@styled";
import * as _ from "lodash";
import ReactSelect, { GroupBase, Props, StylesConfig, components, InputProps, MultiValueProps } from "react-select";
import ReactAsyncSelect, { AsyncProps } from "react-select/async";
import CreatableSelect, { CreatableProps } from "react-select/creatable";

import useUserTheme from "shared/styles/hooks/useUserTheme";
import { ISelectProps, selectStylesWithParameters } from "./model";
import { FixedOption, Option } from "shared/model/Option";
import { ThemeType } from "shared/styles/theme";
import { Utilities } from "shared/utilities";
import colors from "shared/styles/colors";

const useSelectStyles = (props: CommonSelectProps): StylesConfig<Option> => {
    const [theme] = useUserTheme();
    const styles: StylesConfig<Option> = selectStylesWithParameters({
        theme,
        height: props.height,
        width: props.width,
        isLanguageModeSelector: props.isLanguageModeSelector,
        isAgentGroupSelectorWithLinks: props.isAgentGroupSelectorWithLinks,
        borderRadius: props.borderRadius ?? "25px",
        borderWidthLeft: props.borderWidthLeft ?? "2px",
        borderWidthRight: props.borderWidthRight ?? "2px",
        menuMarginLeft: props.menuMarginLeft ?? '0',
        hideDropdownIndicator: props.hideDropdownIndicator
    });

    if (props.styles !== undefined) {
        return { ...styles, ...props.styles }
    }

    return styles;
}

const Select: React.FC<SingleSelectProps> = (props) => {
    const styles = useSelectStyles(props);
    return (
        <ReactSelect
            {...props}
            styles={styles}
        />
    )
}

const multiSelectInputColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.white
})

const StyledMultiSelectInputContainer = styled.div`
    grid-area: 1 / 1 / 2 / 3;

    input {
        color: ${multiSelectInputColor} !important;
    }
`;

const CustomMultiSelectInput = (props: InputProps<Option, true>) => (
    <StyledMultiSelectInputContainer>
        <components.Input {...props} />
    </StyledMultiSelectInputContainer>
)

export const MultiSelect: React.FC<MultiSelectProps> = (props) => {
    const styles = useSelectStyles(props);
    return (
        <ReactSelect
            {...props}
            isMulti={true}
            styles={styles}
            components={{ Input: CustomMultiSelectInput }}
        />
    )
}


export const MultiSelectHideOverFlow: React.FC<MultiSelectHideOverFlowProps> = (props) => {
    const styles = useSelectStyles(props);
    return (
        <ReactSelect
            {...props}
            isMulti={true}
            styles={styles}
            components={{ Input: CustomMultiSelectInput, MultiValue: (prop: MultiValueProps<Option, true, GroupBase<Option>>) => <MultiValue {...prop} maxToShow={props.maxToShow} /> }}
        />
    )
}
interface MoreSelectedBadgeProps {
    items: string[];
}

const MoreSelectedBadge: React.FC<MoreSelectedBadgeProps> = ({ items }) => {
    const style: React.CSSProperties = {
        marginLeft: "auto",
        fontSize: "11px",
        padding: "2px",
        order: 99
    };

    const title = items.join(", ");
    const length = items.length;
    const label = `+ ${length} item${length !== 1 ? "s" : ""} selected`;

    return (
        <div style={style} title={title}>
            {label}
        </div>
    );
};
interface CustomMultiValueProps extends MultiValueProps<Option, true, GroupBase<Option>> {
    maxToShow: number;
}

const MultiValue: React.FC<CustomMultiValueProps> = ({
    index,
    getValue,
    data,
    maxToShow,
    ...props
}) => {
    const overflow = getValue()
        .slice(maxToShow)
        .map((x) => x.label);

    return index < maxToShow ? (
        <components.MultiValue {...props} index={index} getValue={getValue} data={data} />
    ) : index === maxToShow ? (
        <MoreSelectedBadge items={overflow} />
    ) : null;
};


export const AsyncSelect: React.FC<SingleAsyncSelectProps> = (props) => {
    const styles = useSelectStyles(props);
    return (
        <ReactAsyncSelect
            {...props}
            styles={styles}
        />
    )
}

export const MultiAsyncSelect: React.FC<MultiAsyncSelectProps> = (props) => {
    const styles = useSelectStyles(props);
    return (
        <ReactAsyncSelect
            {...props}
            isMulti={true}
            styles={styles}
        />
    )
}

export const Creatable: React.FC<CreatableSelectProps> = (props) => {
    const styles = useSelectStyles(props);
    return (
        <CreatableSelect
            {...props}
            styles={styles}
        />
    )
}

export const MultiCreatable = React.forwardRef((props: MultiCreatableSelectProps, ref: React.Ref<any>) => {
    const styles = useSelectStyles(props);
    return (
        <CreatableSelect
            {...props}
            ref={ref}
            isMulti={true}
            styles={styles}
        />
    )
})

// Component definition is missing display nameeslintreact/display-name for React DevTools
MultiCreatable.displayName = "MultiCreatable";

export const ParameterInputLanguageModeSelector: React.FC<SingleSelectProps> = (props) => {
    const styles = useSelectStyles({ ...props, isLanguageModeSelector: true });
    return (
        <ReactSelect
            {...props}
            isClearable={false}
            menuPlacement="auto"
            styles={styles}
            menuPosition="fixed"
        />
    )
}

export const IntegerDropdown: React.FC<IntegerDropdownProps> = (props) => {
    const [theme] = useUserTheme();
    const handleChange = (newValue: Option) => {
        props.onChange(parseInt(newValue.value, 10));
    };

    const getValueLabel = (i: number) => {
        if (props.zeroPad && i < 10) {
            return "0" + i;
        }
        return i.toString();
    };

    const options: Option[] = _.range(0, props.maxValue).map((i) => ({
        value: i,
        label: getValueLabel(i)
    }));

    const selectStyles: StylesConfig<FixedOption> = {
        control: (provided, state) => ({
            ...provided,
            height: '40px',
            minWidth: '51px',
            borderTop: `2px solid ${S.Colors.grey_2}`,
            borderBottom: `2px solid ${S.Colors.grey_2}`,
            borderLeft: props.isLeftSide ? `2px solid ${S.Colors.grey_2}` : '0 !important',
            borderRight: props.isLeftSide ? '0 !important' : `2px solid ${S.Colors.grey_2}`,
            borderRadius: props.isLeftSide ? '25px 0 0 25px' : '0 25px 25px 0',
            fontSize: '18px',
            boxShadow: 'none',
            "&:hover": {
                borderColor: S.Colors.grey_2
            },
            outline: state.isFocused ? '1px solid black' : '0',
            color: theme === ThemeType.light ? S.Colors.black : S.Colors.white,
            backgroundColor: theme === ThemeType.light ? S.Colors.white : S.Colors.dark2
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: props.isLeftSide ? '0 8px 0 16px' : '0 16px 0 8px',
        }),
        indicatorsContainer: () => ({
            display: 'none',
        }),
    };

    return <Select
        isDisabled={props.isDisabled}
        className="integer-dropdown-selector"
        classNamePrefix="react-select"
        id="environment-select"
        value={Utilities.valueFromId(options, props.selectedValue)}
        styles={selectStyles}
        options={options}
        onChange={handleChange}
    />;
};

export type SingleSelectProps = Props<Option, false, GroupBase<Option>> & ISelectProps;
export type MultiSelectProps = Props<Option, true, GroupBase<Option>> & ISelectProps;
export type MultiSelectHideOverFlowProps = MultiSelectProps & { maxToShow: number }
type SingleAsyncSelectProps = AsyncProps<Option, false, GroupBase<Option>> & ISelectProps;
type MultiAsyncSelectProps = AsyncProps<Option, true, GroupBase<Option>> & ISelectProps;

type CreatableSelectProps = CreatableProps<Option, false, GroupBase<Option>> & ISelectProps;
type MultiCreatableSelectProps = CreatableProps<Option, true, GroupBase<Option>> & ISelectProps;

type CommonSelectProps = SingleSelectProps | MultiSelectProps | SingleAsyncSelectProps | MultiAsyncSelectProps | CreatableSelectProps | MultiCreatableSelectProps;

export interface IntegerDropdownProps {
    selectedValue: number;
    maxValue: number;
    zeroPad: boolean;
    isLeftSide: boolean;
    styles?: React.CSSProperties;
    isDisabled?: boolean;
    onChange: (newValue: number) => void;
}

export default Select;